import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { loadPlaces, mapsConfigured, mapsAuthFailed } from "../lib/googleMaps";

// Address field with Google Places suggestions.
//
// Degrades to a plain text input whenever Places is unavailable — no key, key
// rejected, API not enabled, offline. Typing and submitting must never depend
// on Google being reachable.
export const AddressAutocomplete = ({
  value, onChange, placeholder, testId, className = "", required, minLength,
  country = "ca",
}) => {
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!mapsConfigured() || mapsAuthFailed()) return;
    let cancelled = false;
    let listener = null;
    let widget = null;

    (async () => {
      try {
        const places = await loadPlaces();
        if (cancelled || !inputRef.current) return;
        const Legacy = places.Autocomplete || window.google?.maps?.places?.Autocomplete;
        if (!Legacy) return;
        widget = new Legacy(inputRef.current, {
          fields: ["formatted_address", "name"],
          componentRestrictions: { country },
        });
        listener = widget.addListener("place_changed", () => {
          const place = widget.getPlace();
          const picked = place?.formatted_address || place?.name;
          // Mirror the chosen address back into React state, otherwise the
          // controlled input and the widget's DOM value drift apart.
          if (picked) onChangeRef.current(picked);
        });
        if (!cancelled) setLive(true);
      } catch (e) {
        // Autocomplete is an enhancement; the field keeps working without it.
        if (!cancelled) setLive(false);
      }
    })();

    const onAuthFail = () => setLive(false);
    window.addEventListener("gmaps-auth-failed", onAuthFail);
    return () => {
      cancelled = true;
      window.removeEventListener("gmaps-auth-failed", onAuthFail);
      if (listener?.remove) listener.remove();
      if (widget && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(widget);
      }
    };
  }, [country]);

  return (
    <>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        required={required}
        minLength={minLength}
        autoComplete="off"
        data-testid={testId}
        data-autocomplete={live ? "live" : "plain"}
      />
      {live && (
        <div className="text-[11px] text-muted-foreground mt-1" data-testid={`${testId}-hint`}>
          Start typing and pick your address from the suggestions.
        </div>
      )}
    </>
  );
};

export default AddressAutocomplete;
